import React, { useState } from 'react';
import { VStack, HStack, Text, Input, Button, Tooltip, Box } from '@chakra-ui/react';
import { Hospital } from '../../types';

interface HospitalCardHeaderProps {
  hospital: Hospital;
  onFieldUpdate?: (hospitalId: string, fieldName: string, value: string) => void;
}

const HospitalCardHeader: React.FC<HospitalCardHeaderProps> = ({ hospital, onFieldUpdate }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(hospital.name);
  const [savingName, setSavingName] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editAddress, setEditAddress] = useState(hospital.address);
  const [savingAddress, setSavingAddress] = useState(false);

  const handleNameSave = async () => {
    if (!onFieldUpdate) return;
    setSavingName(true);
    try {
      await onFieldUpdate(hospital._id, 'name', editName);
      setIsEditingName(false);
    } catch (error) {
      console.error('Failed to save name:', error);
    } finally {
      setSavingName(false);
    }
  };

  const handleAddressSave = async () => {
    if (!onFieldUpdate) return;
    setSavingAddress(true);
    try {
      await onFieldUpdate(hospital._id, 'address', editAddress);
      setIsEditingAddress(false);
    } catch (error) {
      console.error('Failed to save address:', error);
    } finally {
      setSavingAddress(false);
    }
  };

  return (
    <VStack align="flex-start" flex={1} spacing={3}>
      {isEditingName ? (
        <HStack spacing={2} align="center" onClick={(e) => e.stopPropagation()}>
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            size="sm"
            flex={1}
            minW={`${Math.max(editName.length * 8, 200)}px`}
            className="name-input"
          />
          <Button size="xs" colorScheme="blue" onClick={handleNameSave} isLoading={savingName}>
            Save
          </Button>
          <Button size="xs" variant="outline" onClick={() => { setEditName(hospital.name); setIsEditingName(false); }}>
            Discard
          </Button>
        </HStack>
      ) : (
        <HStack spacing={2} align="center">
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" flex={1}>
            {hospital.name}
          </Text>
          {hospital.status === 'rejected' && onFieldUpdate && (
            <Button
              size="xs"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingName(true);
              }}
            >
              Edit
            </Button>
          )}
        </HStack>
      )}
      
      {isEditingAddress ? (
        <HStack spacing={2} align="center" onClick={(e) => e.stopPropagation()}>
          <Input
            value={editAddress}
            onChange={(e) => setEditAddress(e.target.value)}
            size="sm"
            flex={1}
            minW={`${Math.max(editAddress.length * 8, 200)}px`}
          />
          <Button size="xs" colorScheme="blue" onClick={handleAddressSave} isLoading={savingAddress}>
            Save
          </Button>
          <Button size="xs" variant="outline" onClick={() => { setEditAddress(hospital.address); setIsEditingAddress(false); }}>
            Discard
          </Button>
        </HStack>
      ) : (
        <HStack spacing={2} align="center">
          <Text color="gray.600" fontSize="sm" flex={1}>
            {hospital.address}
          </Text>
          {hospital.status === 'rejected' && onFieldUpdate && (
            <Button
              size="xs"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingAddress(true);
              }}
            >
              Edit
            </Button>
          )}
        </HStack>
      )}
      
      <HStack spacing={4} fontSize="xs" color="gray.500">
        <Text>Insurance: {hospital.insurance}</Text>
        <Text>Created: {new Date(hospital.createdAt).toLocaleDateString()}</Text>
        <Tooltip
          label={
            hospital.authorizers && hospital.authorizers.length > 0 ? (
              <VStack align="flex-start" spacing={1} p={4} minW="400px">
                <Text fontWeight="bold" color="white" fontSize="md" mb={2}>✨ Workflow Trail</Text>
                {hospital.authorizers.map((auth, index) => (
                  <Box key={index} w="full">
                    <HStack spacing={4} align="center">
                      <Box 
                        w="12px" 
                        h="12px" 
                        borderRadius="full" 
                        bg={index === hospital.authorizers!.length - 1 ? "#10B981" : "#60A5FA"}
                        boxShadow="0 0 0 3px rgba(255, 255, 255, 0.3)"
                      />
                      <VStack align="flex-start" spacing={0} flex={1}>
                        <HStack spacing={3}>
                          <Text color="white" fontWeight="bold" fontSize="sm">{auth.agentName}</Text>
                          <Text color="#FDE047" fontSize="sm" fontWeight="semibold">→ {auth.action}</Text>
                        </HStack>
                        <Text color="rgba(255,255,255,0.8)" fontSize="xs">{new Date(auth.timestamp).toLocaleString()}</Text>
                      </VStack>
                    </HStack>
                    {index < hospital.authorizers!.length - 1 && (
                      <Box ml="6px" mt={0.5} mb={0.5}>
                        <Box w="2px" h="8px" bg="rgba(255,255,255,0.4)" borderRadius="1px" />
                      </Box>
                    )}
                  </Box>
                ))}
              </VStack>
            ) : 'No authorizer trail'
          }
          placement="top"
          hasArrow
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          color="white"
          borderRadius="lg"
          boxShadow="0 10px 25px rgba(102, 126, 234, 0.3)"
        >
          <Text fontWeight="semibold" cursor="help" _hover={{ color: 'blue.600' }}>
            Authorizer: {hospital.authorizers && hospital.authorizers.length > 0 
              ? hospital.authorizers[hospital.authorizers.length - 1].agentName 
              : 'system'} 
          </Text>
        </Tooltip>
        {hospital.status === 'matched' && (
          <Text fontWeight="bold" color="green.600">Insurance Added</Text>
        )}
      </HStack>
    </VStack>
  );
};

export default HospitalCardHeader;